'use client'

import { CSSProperties, MouseEvent, useRef, useState } from 'react'
import ImageCarousel from '@/components/blocks/ImageCarousel'

interface ProductDescriptionProps {
  html: string
  productName: string
  vendor: any
  className?: string
  style?: CSSProperties
}

/**
 * Renders the vendor-authored product description and opens any image inside it
 * full screen on click.
 *
 * The body is injected with dangerouslySetInnerHTML, so its <img> elements are
 * not React nodes and can't carry their own onClick — the click is delegated
 * from the container instead.
 */
export default function ProductDescription({ html, productName, vendor, className = '', style }: ProductDescriptionProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [images, setImages] = useState<string[]>([])
  const [activeImage, setActiveImage] = useState('')
  const [isCarouselOpen, setIsCarouselOpen] = useState(false)

  const handleClick = (e: MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement
    if (target.tagName !== 'IMG') return
    if (target.closest('a')) return // a linked image should follow its link

    // Read the literal attribute, not the resolved .src — ImageCarousel feeds
    // each entry through formatImageURL, which is a no-op only on absolute URLs.
    const src = target.getAttribute('src') || ''
    if (!src) return

    // Collect every image in the body so the carousel can page between them.
    const all = Array.from(containerRef.current?.querySelectorAll('img') || [])
      .map(img => img.getAttribute('src') || '')
      .filter(Boolean)

    setImages(all.includes(src) ? all : [src])
    setActiveImage(src)
    setIsCarouselOpen(true)
  }

  return (
    <>
      <div
        ref={containerRef}
        className={className}
        style={style}
        onClick={handleClick}
        dangerouslySetInnerHTML={{ __html: html }}
      />
      <ImageCarousel
        images={images}
        initialImage={activeImage}
        isOpen={isCarouselOpen}
        onClose={() => setIsCarouselOpen(false)}
        productName={productName}
        vendor={vendor}
      />
    </>
  )
}
